// src/modules/competition/components/CompetitionOverview/index.js
import React, { useState, useEffect } from 'react';
import { Container, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CompetitionDetails from '../CompetitionDetails';
import CompetitorCards from '../CompetitorCards';
import CompetitorsTable from '../CompetitorsTable';
import JoinTeamModal from '../JoinTeamModal';
import TeamMembersModal from '../TeamMembersModal';
import { useCompetitionJoin } from '../../hooks/useCompetitionJoin';
import { useAuth } from '../../../../shared/hooks/useAuth';

const CompetitionOverview = ({
    competitors,
    lowestCompetitor,
    currentPeriod,
    onUsernameClick,
    isUserJoined,
    competitionId,
    competitionName,
    pointTarget,
    ministerOfActivity,
    onJoinSuccess,
    isTeamBased,
    teams: teamsProp,
    winnerCalculationMethod,
}) => {
    const { hasPermission } = useAuth();
    const canCreateTeam = hasPermission('teams:manage');
    const canManageMembers = hasPermission('teams:manage');

    // Local teams state so reassignment updates the table without a page reload
    const [localTeams, setLocalTeams] = useState(teamsProp || []);
    useEffect(() => { setLocalTeams(teamsProp || []); }, [teamsProp]);

    const [showMembersModal, setShowMembersModal] = useState(false);

    const {
        joinCompetition,
        alert,
        showAlert,
        setShowAlert,
        showTeamModal,
        openTeamModal,
        closeTeamModal,
        createTeam,
        joinTeam,
        teamActionLoading,
    } = useCompetitionJoin(competitionId);

    const handleJoinCompetition = async () => {
        const success = await joinCompetition();
        if (success) onJoinSuccess();
    };

    const handleCreateTeam = async (teamName) => {
        await createTeam(teamName, onJoinSuccess);
    };

    const handleJoinTeam = async (teamId) => {
        await joinTeam(teamId, onJoinSuccess);
    };

    const validPointTarget = pointTarget || 0;

    // Lowest team by combined points (for cake giver in team competitions)
    const lowestTeam = isTeamBased && localTeams.length > 0
        ? [...localTeams].sort((a, b) => (a.totalPoints ?? 0) - (b.totalPoints ?? 0))[0]
        : null;

    return (
        <Container className="mt-3">
            {alert.show && showAlert && (
                <Alert
                    variant={alert.variant}
                    onClose={() => setShowAlert(false)}
                    dismissible
                >
                    {alert.message}
                </Alert>
            )}

            <CompetitionDetails
                currentPeriod={currentPeriod}
                pointTarget={validPointTarget}
                isUserJoined={isUserJoined}
                onJoinClick={handleJoinCompetition}
                onJoinTeamClick={openTeamModal}
                onManageMembersClick={() => setShowMembersModal(true)}
                competitionId={competitionId}
                competitionName={competitionName}
                isTeamBased={isTeamBased}
                winnerCalculationMethod={winnerCalculationMethod}
            />

            <CompetitorCards
                ministerOfActivity={ministerOfActivity}
                lowestCompetitor={lowestCompetitor}
                isTeamBased={isTeamBased}
                lowestTeam={lowestTeam}
            />

            <CompetitorsTable
                competitors={competitors}
                lowestCompetitor={lowestCompetitor}
                pointTarget={validPointTarget}
                onUsernameClick={onUsernameClick}
                isTeamBased={isTeamBased}
                teams={localTeams}
            />

            {isTeamBased && (
                <JoinTeamModal
                    show={showTeamModal}
                    onHide={closeTeamModal}
                    teams={localTeams}
                    onCreateTeam={handleCreateTeam}
                    onJoinTeam={handleJoinTeam}
                    isLoading={teamActionLoading}
                    canCreateTeam={canCreateTeam}
                />
            )}

            {isTeamBased && canManageMembers && (
                <TeamMembersModal
                    show={showMembersModal}
                    onHide={() => setShowMembersModal(false)}
                    competitionId={competitionId}
                    competitors={competitors}
                    teams={localTeams}
                    onTeamsUpdate={setLocalTeams}
                />
            )}
        </Container>
    );
};

CompetitionOverview.propTypes = {
    competitors: PropTypes.arrayOf(PropTypes.shape({
        username: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        totalPoints: PropTypes.number.isRequired,
        meetsCriteria: PropTypes.bool.isRequired,
        progress: PropTypes.number.isRequired,
    })).isRequired,
    lowestCompetitor: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        profilePicture: PropTypes.string,
        totalPoints: PropTypes.number.isRequired,
    }),
    currentPeriod: PropTypes.shape({
        startDate: PropTypes.string.isRequired,
        endDate: PropTypes.string.isRequired,
    }).isRequired,
    onUsernameClick: PropTypes.func.isRequired,
    isUserJoined: PropTypes.bool.isRequired,
    competitionId: PropTypes.string.isRequired,
    competitionName: PropTypes.string.isRequired,
    isTeamBased: PropTypes.bool,
    teams: PropTypes.array,
    winnerCalculationMethod: PropTypes.string,
};

export default CompetitionOverview;
